// What the multi costs, in dollars.
//
// A multi is four bets folded into one, and the folding is not free: every leg
// is taken at ONE book's price, and the same four selections priced one by one
// — each at whichever book is longest on it — come to more.
//
// BOTH HALVES ARE PRINTED, THE SHARE FIRST. "9.0% off best price · benchmark
// $18.35 (−$1.65)": the percentage is what compares two tickets, because the
// dollars follow the stake and $1.65 off a $10 slip is not the fact $1.65 off a
// $100 slip is; the dollars follow it because nobody bets a percentage and a
// share with no money beside it is a number to do arithmetic on before it means
// anything. This is that arithmetic, done.
//
// THE SECOND FIGURE IS A BENCHMARK, NOT A PAYOUT. Rolling singles is the same
// arithmetic as a multi; the whole gap here is SHOPPING, each leg at its own
// best book. Nobody collects it: the games overlap, the prices move, and it
// takes a funded account at every book on the list. What singles really carry
// is optionality (you can stop after a win) and less margin, and a better price
// makes a losing bet lose less rather than making it a winning one. The ticket
// copy says so in those words, and this file must not be quoted as if it were
// money forgone.
//
// It is INFORMATION, not an alarm — see the note on the ticket's own line. The
// four singles are a different bet with a different variance, not a better
// version of this one, and the app does not tell anybody which to take.
//
// Pure: no refs, no storage, no clock, no fetch. It is handed the multi, its
// legs and the board the app already holds, and it does arithmetic — the same
// shape `lib/hedge.js` is written in, down to rounding the difference FROM the
// two rounded figures so `pays − bestPays = diff` still adds up on screen.

import { bestPriceToTake, priceAt, rowAge, ttlFor } from './engine/universe.js';
import { bookmakerSite } from './bookmakers.js';
import { matchFor } from './live-prices.js';

const cents = (n) => Math.round(n * 100) / 100;

/**
 * HOW OLD THE BENCHMARK MAY BE BEFORE IT SAYS SO: half an hour.
 *
 * Under that, the prices behind it are this morning's board and the line stands
 * on its own. Past it the ticket prints "· as at 9:11 am" — the figure is still
 * arithmetic on real quotes, but it is arithmetic on quotes from a while ago,
 * and a benchmark is only honest while the prices it shops are live. Past the
 * row's own TTL (`STALE_TTL_MS`, engine review #9) the line does not print at
 * all: an old best price is usually a long price nobody is paying any more, so
 * a stale benchmark OVERSTATES the gap, which is the wrong direction to be
 * wrong in about a ticket somebody already holds.
 */
export const BENCHMARK_AS_AT_MS = 30 * 60 * 1000;

/** The row behind a book's price on a selection, for its capture time and name. */
const rowFor = (match, selection, bookmaker) =>
  match?.prices?.[selection]?.find((r) => r.bookmaker === bookmaker) ?? null;

/** What a row's book is called: its own name, the table's, or the bare key. */
const nameOf = (row, key) => row?.bookmakerName ?? bookmakerSite(key)?.name ?? key;

/**
 * THE BEST PRICE A PUNTER COULD TAKE ON ONE LEG, where it is not the leg's own
 * book: `{ bookmakerKey, bookmakerName, price }`, else null.
 *
 * The leg-by-leg half of the line below, for the ticket screen's leg rows: a
 * multi prints one figure for the whole slip, and "best 1.52 at Neds" beside
 * the leg says WHICH leg the figure came from. Null where the leg's own book is
 * top of the board (there is nothing to shop, so there is nothing to say), and
 * null on a leg that is off the board or carries no side, exactly as the line
 * is. The exchange is excluded here for the same reason it is excluded there.
 */
export function bestLegPrice(leg, universe) {
  const match = matchFor(universe, leg);
  if (!match || !leg?.side) return null;
  const best = bestPriceToTake(match, leg.side);
  if (!best || !Number.isFinite(best.price) || best.price <= 1) return null;
  if (best.bookmaker === leg.bookmakerKey) return null;
  // Not a price the leg could be improved on: a book that is not the leg's own
  // and is not paying more is a different name for the same bet.
  if (Number.isFinite(leg.price) && best.price <= leg.price) return null;
  return {
    bookmakerKey: best.bookmaker,
    bookmakerName: nameOf(rowFor(match, leg.side, best.bookmaker), best.bookmaker),
    price: best.price,
  };
}

/**
 * The margin on a multi: `{ pays, bestPays, off, diff, legsPriced, pricedAt }`,
 * or null.
 *
 *   pays        what the ticket returns — `multi.potentialReturn`, the record.
 *   bestPays    THE BENCHMARK: the same stake through the same legs one by one,
 *               each at the longest price on today's board that is a bookmaker.
 *               It is what the one-slip price is measured against, not a sum
 *               anybody could have collected — see the head of this file.
 *   off         `(bestPays − pays) / bestPays`, the gap as a share of the
 *               benchmark — how much of the best price on the board this slip
 *               gives up. It leads the line because it is the figure that
 *               compares across tickets: the dollars follow the stake, and $1.65
 *               off a $10 bet and off a $100 bet are not the same fact. Zero or
 *               less where the ticket's own book led the board on every leg.
 *   diff        `pays − bestPays`, in dollars. Negative is the usual case.
 *   legsPriced  how many legs the benchmark stands on.
 *   pricedAt    epoch ms of the OLDEST live quote behind the benchmark, or null
 *               where no row carries a capture time. The ticket prints it as
 *               "as at 9:11 am" once it is more than `BENCHMARK_AS_AT_MS` old.
 *
 * Null — and the ticket prints nothing rather than a half-answer — when:
 *
 *   · the ticket is a SINGLE. One leg taken one at a time is the same bet, so
 *     the line would compare a number with itself.
 *   · ANY leg is off the board. A ticket from yesterday cannot be re-shopped,
 *     and pricing three of its four legs and quietly dropping the fourth would
 *     print a figure that is not the ticket.
 *   · ANY of the prices behind the benchmark is past its own TTL, which is per
 *     sport and per book (`ttlFor`): six hours on a fixture, ninety minutes on a
 *     race. See `BENCHMARK_AS_AT_MS` above for why silence is the right answer
 *     there rather than an older number.
 *
 * `asOfMs` is the caller's clock — the store's ticking `now` on a screen — and
 * only the two staleness answers depend on it. A board whose rows carry no
 * `capturedAt`, and a row that is not a live quote, are ageless by `rowAge`'s
 * own rule and the line prints exactly as it always did.
 *
 * EXCHANGES ARE NOT IN "BEST". `bestPriceToTake` is the singles leg rule's own
 * exclusion (`engine/universe.js`): the exchange charges commission on the win
 * and may not be there in your size, so its price is not one this line can
 * claim a punter could have had.
 */
export function marginLine(multi, legs, universe, asOfMs = Date.now()) {
  const list = Array.isArray(legs) ? legs : [];
  if (!multi || list.length < 2) return null;

  // The raw fields, not `Number(…)` of them: a void tier carries a null return
  // and `Number(null)` is 0, which would print "100.0% off best price ·
  // benchmark $37.80 (−$37.80)" on a ticket that has no return at all.
  const { stake, potentialReturn: pays } = multi;
  if (!Number.isFinite(stake) || stake <= 0 || !Number.isFinite(pays)) return null;

  let product = 1;
  let pricedAt = null;
  for (const leg of list) {
    const match = matchFor(universe, leg);
    if (!match || !leg?.side) return null;
    const best = bestPriceToTake(match, leg.side);
    // A price at or below evens is a placeholder, not an offer — the same bar
    // `hedge.js` holds every price it multiplies to.
    if (!best || !Number.isFinite(best.price) || best.price <= 1) return null;
    const age = rowAge(rowFor(match, leg.side, best.bookmaker), asOfMs);
    if (Number.isFinite(age)) {
      if (age > ttlFor(match.sport, best.bookmaker)) return null;
      const at = asOfMs - age;
      if (pricedAt === null || at < pricedAt) pricedAt = at;
    }
    product *= best.price;
  }

  const bestPays = cents(stake * product);
  const paid = cents(pays);
  // Off the ROUNDED figures, so the percentage is the one a reader can work out
  // from the two dollar amounts printed beside it.
  const off = bestPays > 0 ? (bestPays - paid) / bestPays : 0;
  return { pays: paid, bestPays, off, diff: cents(paid - bestPays), legsPriced: list.length, pricedAt };
}

/**
 * THE ONE BOOK A TICKET IS PLACED AT, or null where its legs are spread.
 *
 * A multi that names a book (`bookmakerKey`) says so itself; anything else has
 * to be read off the legs, and only counts where every one of them agrees. A
 * ticket whose legs sit at three shops cannot be compared to "singles at the
 * same book", because there is no same book.
 */
export function oneBookOf(multi, legs) {
  if (multi?.bookmakerKey) return multi.bookmakerKey;
  const list = Array.isArray(legs) ? legs : [];
  const keys = new Set(list.map((leg) => leg?.bookmakerKey ?? null));
  const [only] = [...keys];
  return keys.size === 1 && only ? only : null;
}

/**
 * The same legs, one at a time, at THIS TICKET'S OWN BOOK — the second half of
 * the reviewer's "a multi that cannot beat its singles says so".
 *
 * `marginLine` above compares the ticket to the best price on the board, which
 * is a fact about shopping. This compares it to the one counter it is actually
 * being placed at, which is a fact about the ticket: same book, same legs, same
 * stake rolled through.
 *
 * WHAT THE COMPOUNDED MARGIN EXPLAINS HERE IS NOTHING. Both sides of this
 * comparison carry the same books' margins on the same legs, so the margin
 * cancels out and the two figures should be the same number. When they are not,
 * the ticket's own prices have moved away from what that book is paying now —
 * and the caller's job is only to ignore a gap small enough to be the rounding
 * on a combined price (`combineLegs` rounds the product to cents).
 *
 * Null on a single, on a ticket with no one book, and on any ticket whose legs
 * are not all on the board it was handed — the same three refusals `marginLine`
 * makes, for the same reason.
 */
export function sameBookLine(multi, legs, universe) {
  const list = Array.isArray(legs) ? legs : [];
  if (!multi || list.length < 2) return null;
  const book = oneBookOf(multi, list);
  if (!book) return null;

  const { stake, potentialReturn: pays } = multi;
  if (!Number.isFinite(stake) || stake <= 0 || !Number.isFinite(pays)) return null;

  let product = 1;
  for (const leg of list) {
    const match = matchFor(universe, leg);
    if (!match || !leg?.side) return null;
    const price = priceAt(match, leg.side, book);
    if (!Number.isFinite(price) || price <= 1) return null;
    product *= price;
  }

  const samePays = cents(stake * product);
  const paid = cents(pays);
  return { book, pays: paid, samePays, diff: cents(paid - samePays), legsPriced: list.length };
}

/**
 * How far apart the two figures have to be before the gap is worth a line: a
 * cent, or half a per cent of the figure, whichever is larger. Below that it is
 * the rounding on a combined price, not a different bet.
 */
export const SAME_BOOK_TOLERANCE = 0.005;

/** The same legs at the same book pay MORE than this ticket does, materially. */
export function sameBookPaysMore(line) {
  if (!line) return false;
  return -line.diff > Math.max(0.01, SAME_BOOK_TOLERANCE * line.samePays);
}
